<template>
  <modal
    :name="modalName"
    height="auto"
    :width="isEditMode ? '1200' : '700'"
    :scrollable="true"
    :clickToClose="false"
  >
    <CloseModal :close="close" />
    <div class="modalbox">
      <div class="modalbox-content">
        <div class="form-body">
          <div class="row">
            <!-- Coluna Esquerda: Dados Básicos -->
            <div :class="isEditMode ? 'col-md-4' : 'col-12'">
              <div class="basic-info-section">
                <div class="section-label">Informações Básicas</div>

                <div class="form-group">
                  <label>Descrição <span class="text-danger">*</span></label>
                  <input
                    class="form-control"
                    v-model="localItem.descricao"
                    placeholder="Ex: Gerente de Vendas"
                    :disabled="isSubmitting"
                  />
                  <div class="text-danger small mt-1">
                    {{ validation.firstError('localItem.descricao') }}
                  </div>
                </div>

                <div class="form-group">
                  <label>Status</label>
                  <div class="toggle-row">
                    <label
                      class="toggle-item"
                      :class="{ active: localItem.ativo == 1 }"
                      @click="localItem.ativo = localItem.ativo == 1 ? 0 : 1"
                    >
                      <input type="checkbox" :checked="localItem.ativo == 1" :disabled="isSubmitting" />
                      {{ localItem.ativo == 1 ? 'Ativo' : 'Inativo' }}
                    </label>
                  </div>
                </div>

                <div class="form-group">
                  <label>Tipos de Colaborador</label>
                  <div class="tipos-wrapper">
                    <div
                      v-for="tipoColaborador in tiposColaborador"
                      :key="tipoColaborador.id"
                      class="tipo-checkbox-item"
                      :class="{
                        active: isTipoSelecionado(tipoColaborador.id),
                        'has-extra-content': tipoColaborador.chave === 'CLIENTE' && isTipoSelecionado(tipoColaborador.id)
                      }"
                    >
                      <label
                        class="tipo-label"
                        @click.prevent="toggleTipo(tipoColaborador.id)"
                      >
                        <input
                          type="checkbox"
                          :checked="isTipoSelecionado(tipoColaborador.id)"
                          :disabled="isSubmitting"
                        />
                        <span>{{ tipoColaborador.desc }}</span>
                      </label>

                      <div
                        v-if="tipoColaborador.chave === 'CLIENTE' && isTipoSelecionado(tipoColaborador.id)"
                        class="flag-select-wrapper"
                      >
                        <label class="flag-label">Flag do Cliente:</label>
                        <select
                          v-model="localItem.flags"
                          class="form-control"
                          :disabled="isSubmitting"
                        >
                          <option :value="null">Selecione uma flag</option>
                          <option
                            v-for="option in options"
                            :key="option.chave"
                            :value="option.chave"
                          >
                            {{ option.name }}
                          </option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Coluna Direita: Permissões (apenas no modo Edit) -->
            <div v-if="isEditMode" class="col-md-8">
              <div class="permissions-section">
                <div class="permissions-header">
                  <div class="section-label mb-0">Gerenciamento de Permissões</div>
                  <div class="permissions-actions">
                    <CButton color="light" size="sm" @click="expandAll" class="mr-2">
                      Expandir Tudo
                    </CButton>
                    <CButton color="light" size="sm" @click="collapseAll">
                      Recolher Tudo
                    </CButton>
                  </div>
                </div>

                <!-- Busca de Módulos -->
                <div class="permissions-search mb-3">
                  <div class="input-group">
                    <div class="input-group-prepend">
                      <span class="input-group-text">
                        <fa-icon icon="search" />
                      </span>
                    </div>
                    <input
                      type="text"
                      class="form-control"
                      v-model="searchModulo"
                      placeholder="Buscar módulos..."
                    />
                    <div class="input-group-append" v-if="searchModulo">
                      <button class="btn btn-outline-secondary" @click="searchModulo = ''">
                        <fa-icon icon="times" />
                      </button>
                    </div>
                  </div>
                </div>

                <!-- Tabs de Sistemas -->
                <CTabs variant="tabs" class="permissions-tabs">
                  <CTab
                    v-for="sistema in sistemasVisiveis"
                    :key="sistema.id"
                  >
                    <template #title>
                      <span class="tab-title">
                        {{ sistema.descricao }}
                        <CBadge color="primary" class="ml-2">
                          {{ getPermissoesCountBySistema(sistema) }}
                        </CBadge>
                      </span>
                    </template>

                    <div class="permissions-content">
                      <div
                        v-if="getFilteredModulos(sistema.modulos).length === 0"
                        class="empty-search"
                      >
                        <fa-icon icon="search" size="2x" class="text-muted mb-2" />
                        <p class="text-muted mb-0">Nenhum módulo encontrado</p>
                      </div>

                      <CCard
                        v-for="modulo in getFilteredModulos(sistema.modulos)"
                        :key="modulo.id"
                        class="modulo-card"
                        :class="{ 'modulo-active': isModuloIncluso(modulo) }"
                      >
                        <CCardHeader class="modulo-header" @click="handleChangePermissao(modulo)">
                          <div class="modulo-info">
                            <label class="modulo-checkbox">
                              <input
                                type="checkbox"
                                :checked="isModuloIncluso(modulo)"
                                :disabled="isLoadingModulo(modulo)"
                              />
                              <span class="modulo-name">{{ modulo.descPlural }}</span>
                            </label>

                            <div class="modulo-actions" @click.stop>
                              <div v-show="isLoadingModulo(modulo)" class="mr-3">
                                <div class="spinner-border spinner-border-sm text-primary" role="status">
                                  <span class="sr-only">Carregando...</span>
                                </div>
                              </div>

                              <CBadge
                                v-if="modulo.moduloAcoes.length > 0 && isModuloIncluso(modulo)"
                                :color="getAcoesActiveBadgeColor(modulo)"
                                class="mr-2"
                              >
                                {{ getAcoesActiveCount(modulo) }} / {{ modulo.moduloAcoes.length }} ações
                              </CBadge>

                              <CButton
                                v-if="modulo.moduloAcoes.length > 0 && isModuloIncluso(modulo)"
                                color="link"
                                size="sm"
                                @click="toggleModuloAccordion(modulo.id)"
                                class="p-0"
                              >
                                <fa-icon
                                  :icon="isModuloExpanded(modulo.id) ? 'chevron-up' : 'chevron-down'"
                                  size="lg"
                                />
                              </CButton>
                            </div>
                          </div>
                        </CCardHeader>

                        <CCollapse
                          :show="isModuloExpanded(modulo.id)"
                          v-if="modulo.moduloAcoes.length > 0 && isModuloIncluso(modulo)"
                        >
                          <CCardBody class="acoes-body">
                            <div class="acoes-header mb-3">
                              <span class="acoes-title">Ações Disponíveis</span>
                              <CButton
                                color="link"
                                size="sm"
                                @click="toggleAllAcoes(modulo)"
                                class="p-0"
                              >
                                {{ areAllAcoesActive(modulo) ? 'Desmarcar' : 'Marcar' }} todas
                              </CButton>
                            </div>

                            <div class="acoes-grid">
                              <div
                                v-for="moduloAcao in modulo.moduloAcoes"
                                :key="moduloAcao.id"
                                class="acao-item"
                                :class="{ active: isModuloAcaoIncluso(moduloAcao.id) }"
                                @click="handleChangePermissaoModuloAcoes(moduloAcao, localItem, modulo)"
                              >
                                <input
                                  type="checkbox"
                                  :checked="isModuloAcaoIncluso(moduloAcao.id)"
                                />
                                <span>{{ moduloAcao.descPlural }}</span>
                              </div>
                            </div>
                          </CCardBody>
                        </CCollapse>
                      </CCard>
                    </div>
                  </CTab>
                </CTabs>

                <!-- Resumo de Permissões -->
                <div class="permissions-summary">
                  <strong>Resumo:</strong>
                  {{ getTotalPermissoesAtivas() }} módulos ativos com {{ getTotalAcoesAtivas() }} ações permitidas
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer FORA do modalbox -->
    <div class="modal-footer-bar">
      <CButton variant="outline" color="secondary" @click="close" :disabled="isSubmitting">
        Cancelar
      </CButton>
      <CButton color="primary" :disabled="validation.hasError() || isSubmitting" @click="handleSubmit">
        <span v-if="isSubmitting">Salvando...</span>
        <span v-else>{{ isEditMode ? 'Salvar' : 'Criar Perfil' }}</span>
      </CButton>
    </div>
  </modal>
</template>

<script>
import { Validator } from 'simple-vue-validator';
import _findIndex from 'lodash/findIndex';
import CloseModal from '../../components/CloseModal';

export default {
  name: 'PerfilFormModal',
  components: { CloseModal },
  props: {
    mode: {
      type: String,
      required: true,
      validator: (value) => ['create', 'edit'].includes(value)
    },
    item: {
      type: Object,
      default: () => ({})
    },
    access: {
      type: Object,
      default: () => ({})
    },
    sistemas: {
      type: [Array, Object],
      default: () => []
    },
    tiposColaborador: {
      type: Array,
      required: true
    },
    descSingular: {
      type: String,
      required: true
    },
    onSubmit: {
      type: Function,
      required: true
    },
    close: {
      type: Function,
      required: true
    },
    addAcesso: Function,
    remAcesso: Function,
    addAcessoModuloAcoes: Function,
    remAcessoModuloAcoes: Function,
  },
  data() {
    const initialItem = this.getInitialItem();
    if (!initialItem.hasOwnProperty('flags')) {
      initialItem.flags = null;
    }
    return {
      localItem: initialItem,
      loadingModulos: [],
      expandedModulos: [],
      searchModulo: '',
      isSubmitting: false,
      options: [
        { name: 'Comprador (Nível 1)', chave: 'COMPRADOR' },
        { name: 'Gestor de Loja (Nível 2)', chave: 'GESTOR_NIVEL_2' },
        { name: 'Gestor Geral (Nível 3)', chave: 'GESTOR_NIVEL_3' }
      ]
    };
  },
  computed: {
    isEditMode() {
      return this.mode === 'edit';
    },
    modalName() {
      return this.isEditMode ? 'perfilEdit' : 'perfilCreate';
    },
    sistemasVisiveis() {
      if (!this.sistemas) return [];
      let sistemasArray = Array.isArray(this.sistemas)
        ? this.sistemas
        : Object.values(this.sistemas);
      const sistemasOcultos = ['B2B_APP', 'B2B_APP_CLIENTE_FINAL'];
      return sistemasArray.filter(sistema =>
        sistema && sistema.chave && !sistemasOcultos.includes(sistema.chave)
      );
    },
    pivots() {
      if (this.access && this.access.perfisModulos) {
        return this.access.perfisModulos.reduce((acc, pivot) => {
          acc.push(pivot.modulo_id);
          return acc;
        }, []);
      }
      return [];
    }
  },
  validators: {
    'localItem.descricao': (value) => Validator.value(value).required('A descrição é obrigatória'),
    'localItem.ativo': (value) => Validator.value(value).required('O status é obrigatório'),
  },
  watch: {
    item: {
      handler(newVal, oldVal) {
        if (this.isEditMode && newVal && newVal !== oldVal) {
          const currentFlags = this.localItem.flags;
          this.localItem = { ...newVal };
          if (currentFlags && !this.localItem.flags) {
            this.$set(this.localItem, 'flags', currentFlags);
          }
        }
      },
      deep: true
    }
  },
  methods: {
    getInitialItem() {
      if (this.isEditMode && this.item) {
        return { ...this.item };
      }
      return {
        fk_sistema: '',
        descricao: '',
        ativo: 1,
        perfisTipos: [],
        flags: null
      };
    },
    isTipoSelecionado(idTipoColaborador) {
      if (!this.localItem.perfisTipos) return false;
      return _findIndex(this.localItem.perfisTipos, (t) => +t === +idTipoColaborador) >= 0;
    },
    toggleTipo(tipoId) {
      if (this.isSubmitting) return;
      const currentFlags = this.localItem.flags;
      if (!this.localItem.perfisTipos) {
        this.$set(this.localItem, 'perfisTipos', []);
      }
      const index = _findIndex(this.localItem.perfisTipos, (t) => +t === +tipoId);
      if (index >= 0) {
        this.localItem.perfisTipos.splice(index, 1);
      } else {
        this.localItem.perfisTipos.push(+tipoId);
      }
      if (currentFlags && !this.localItem.flags) {
        this.$set(this.localItem, 'flags', currentFlags);
      }
    },
    handleSubmit() {
      this.$validate().then((success) => {
        if (success) {
          this.isSubmitting = true;
          this.onSubmit(this.localItem);
          setTimeout(() => {
            this.isSubmitting = false;
          }, 1000);
          if (!this.isEditMode) {
            this.localItem = this.getInitialItem();
          }
        }
      });
    },

    // Métodos de Permissões
    getFilteredModulos(modulos) {
      if (!this.searchModulo) return modulos;
      const term = this.searchModulo.toLowerCase();
      return modulos.filter(m => m.descPlural.toLowerCase().includes(term));
    },
    isModuloExpanded(moduloId) {
      return this.expandedModulos.includes(moduloId + '' + this.localItem.id);
    },
    toggleModuloAccordion(moduloId) {
      const key = moduloId + '' + this.localItem.id;
      const index = this.expandedModulos.indexOf(key);
      if (index >= 0) {
        this.expandedModulos.splice(index, 1);
      } else {
        this.expandedModulos.push(key);
      }
    },
    expandAll() {
      this.sistemasVisiveis.forEach(sistema => {
        sistema.modulos.forEach(modulo => {
          if (modulo.moduloAcoes.length > 0 && this.isModuloIncluso(modulo)) {
            const key = modulo.id + '' + this.localItem.id;
            if (!this.expandedModulos.includes(key)) {
              this.expandedModulos.push(key);
            }
          }
        });
      });
    },
    collapseAll() {
      this.expandedModulos = [];
    },
    isModuloIncluso(modulo) {
      return this.pivots.indexOf(+modulo.id) >= 0;
    },
    isLoadingModulo(modulo) {
      return this.loadingModulos.indexOf(modulo.id) >= 0;
    },
    isModuloAcaoIncluso(moduloAcaoId) {
      if (!this.access.perfisModulosAcoes) return false;
      return !!this.access.perfisModulosAcoes.find(
        (moduloAcao) => moduloAcao.modulo_acao_id == moduloAcaoId
      );
    },
    removeLoadingModulo(modulo) {
      const idx = this.loadingModulos.indexOf(modulo.id);
      if (idx >= 0) {
        this.loadingModulos.splice(idx, 1);
      }
    },
    handleChangePermissao(modulo) {
      this.loadingModulos.push(modulo.id);
      if (!this.isModuloIncluso(modulo)) {
        this.addAcesso(this.localItem, this.localItem.id, modulo.id).then(() =>
          this.removeLoadingModulo(modulo)
        );
      } else {
        const key = modulo.id + '' + this.localItem.id;
        const index = this.expandedModulos.indexOf(key);
        if (index >= 0) {
          this.expandedModulos.splice(index, 1);
        }
        this.remAcesso(this.localItem, this.localItem.id, modulo.id).then(() =>
          this.removeLoadingModulo(modulo)
        );
      }
    },
    toggleAllAcoes(modulo) {
      const allActive = this.areAllAcoesActive(modulo);
      modulo.moduloAcoes.forEach((acao) => {
        const isActive = this.isModuloAcaoIncluso(acao.id);
        if (allActive && isActive) {
          this.handleChangePermissaoModuloAcoes(acao, this.localItem, modulo);
        } else if (!allActive && !isActive) {
          this.handleChangePermissaoModuloAcoes(acao, this.localItem, modulo);
        }
      });
    },
    handleChangePermissaoModuloAcoes(moduloAcao, perfil, modulo) {
      if (this.isModuloAcaoIncluso(moduloAcao.id)) {
        this.remAcessoModuloAcoes(perfil, perfil.id, modulo.id, moduloAcao.id);
      } else {
        this.addAcessoModuloAcoes(perfil, perfil.id, modulo.id, moduloAcao.id);
      }
    },
    getAcoesActiveCount(modulo) {
      return modulo.moduloAcoes.filter(acao => this.isModuloAcaoIncluso(acao.id)).length;
    },
    areAllAcoesActive(modulo) {
      return this.getAcoesActiveCount(modulo) === modulo.moduloAcoes.length;
    },
    getAcoesActiveBadgeColor(modulo) {
      const count = this.getAcoesActiveCount(modulo);
      const total = modulo.moduloAcoes.length;
      if (count === 0) return 'secondary';
      if (count === total) return 'success';
      return 'warning';
    },
    getPermissoesCountBySistema(sistema) {
      return sistema.modulos.filter(m => this.isModuloIncluso(m)).length;
    },
    getTotalPermissoesAtivas() {
      return this.pivots.length;
    },
    getTotalAcoesAtivas() {
      if (!this.access.perfisModulosAcoes) return 0;
      return this.access.perfisModulosAcoes.length;
    },
  },
};
</script>

<style scoped>
/* ===== FORM BODY ===== */
.form-body { padding: 16px 20px; }

/* ===== SECTION LABEL (padrão Vitrines) ===== */
.section-label {
  font-size: 11px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

/* ===== COMPACT FORM GROUP ===== */
.form-body .form-group { margin-bottom: 10px; }

.form-body .form-group > label {
  font-size: 12px;
  font-weight: 500;
  color: #344054;
  margin-bottom: 3px;
}

/* ===== BASIC INFO SECTION ===== */
.basic-info-section {
  background: #f8f9fa;
  padding: 1.25rem;
  border-radius: 10px;
  border: 1px solid #e4e7eb;
  height: 100%;
}

/* ===== TOGGLE ROW (padrão Vitrines) ===== */
.toggle-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.toggle-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #344054;
  transition: all 0.15s;
  user-select: none;
}

.toggle-item:hover { background: #f9fafb; }

.toggle-item.active {
  border-color: #3a3e81;
  background: #f2f2f8;
  color: #3a3e81;
}

.toggle-item input[type="checkbox"] {
  accent-color: #3a3e81;
  pointer-events: none;
}

/* ===== TIPOS COLABORADOR ===== */
.tipos-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.tipo-checkbox-item {
  display: flex;
  flex-direction: column;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  transition: all 0.15s;
  background: #fff;
}

.tipo-checkbox-item:hover {
  border-color: #b0bec5;
}

.tipo-checkbox-item.active {
  border-color: #3a3e81;
  background: #f2f2f8;
}

.tipo-label {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 13px;
  color: #344054;
  font-weight: 500;
  margin: 0;
}

.tipo-label input[type="checkbox"] {
  accent-color: #3a3e81;
}

.flag-select-wrapper {
  padding: 0 12px 10px 12px;
}

.flag-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  margin-bottom: 4px;
}

.flag-select-wrapper .form-control {
  font-size: 13px;
  padding: 6px 10px;
  border: 1px solid #e4e7eb;
  border-radius: 6px;
}

/* ===== PERMISSIONS SECTION ===== */
.permissions-section {
  background: white;
  padding: 1.25rem;
  border-radius: 10px;
  border: 1px solid #e4e7eb;
}

.permissions-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.permissions-actions {
  display: flex;
  gap: 6px;
}

.permissions-search {
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
  padding-bottom: 4px;
}

/* ===== PERMISSIONS TABS ===== */
.permissions-tabs { margin-top: 10px; }

.tab-title {
  display: flex;
  align-items: center;
}

.permissions-content { padding: 12px 0; }

.empty-search {
  text-align: center;
  padding: 40px 16px;
}

/* ===== MODULE CARD ===== */
.modulo-card {
  margin-bottom: 8px;
  border: 1px solid #e4e7eb;
  border-radius: 10px;
  transition: all 0.15s;
  overflow: hidden;
}

.modulo-card.modulo-active {
  border-color: #3a3e81;
  background: #f2f2f8;
}

.modulo-header {
  background: #f9fafb;
  border-bottom: 1px solid #e4e7eb;
  padding: 0;
  cursor: pointer;
}

.modulo-card.modulo-active .modulo-header {
  background: #eceaf3;
}

.modulo-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 48px;
}

.modulo-checkbox {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  cursor: pointer;
  margin: 0;
}

.modulo-checkbox input[type="checkbox"] {
  accent-color: #3a3e81;
  flex-shrink: 0;
}

.modulo-name {
  font-size: 13px;
  font-weight: 600;
  color: #344054;
  line-height: 1.4;
  word-break: break-word;
}

.modulo-actions {
  display: flex;
  align-items: center;
  padding: 10px 14px 10px 0;
  flex-shrink: 0;
}

/* ===== AÇÕES ===== */
.acoes-body {
  background: #fafbfc;
  padding: 14px;
}

.acoes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #e9ecef;
}

.acoes-title {
  font-size: 11px;
  font-weight: 700;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.acoes-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 8px;
}

.acao-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #344054;
  transition: all 0.15s;
  background: #fff;
}

.acao-item:hover {
  border-color: #b0bec5;
  background: #f9fafb;
}

.acao-item.active {
  border-color: #3a3e81;
  background: #f2f2f8;
  color: #3a3e81;
}

.acao-item input[type="checkbox"] {
  accent-color: #3a3e81;
  pointer-events: none;
  flex-shrink: 0;
}

/* ===== PERMISSIONS SUMMARY ===== */
.permissions-summary {
  margin-top: 12px;
  padding: 10px 14px;
  background: #edf8f9;
  border: 1px solid #c5e6e9;
  border-radius: 8px;
  text-align: center;
  font-size: 13px;
  color: #2298a3;
}

/* ===== MODAL FOOTER (fora do modalbox) ===== */
.modal-footer-bar {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid #e9ecef;
  background: #fff;
}

/* ===== TABS SCROLLABLE ===== */
.permissions-tabs ::v-deep .nav-tabs {
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
}

.permissions-tabs ::v-deep .nav-tabs::-webkit-scrollbar { height: 6px; }
.permissions-tabs ::v-deep .nav-tabs::-webkit-scrollbar-track { background: #f7fafc; }
.permissions-tabs ::v-deep .nav-tabs::-webkit-scrollbar-thumb { background: #cbd5e0; border-radius: 3px; }

.permissions-tabs ::v-deep .nav-item {
  flex-shrink: 0;
  min-width: fit-content;
}

.permissions-tabs ::v-deep .nav-link {
  white-space: nowrap;
  padding: 0.6rem 1rem;
  font-weight: 500;
  font-size: 13px;
  color: #667085;
  border: none;
  border-bottom: 3px solid transparent;
  transition: all 0.15s;
}

.permissions-tabs ::v-deep .nav-link:hover {
  color: #3a3e81;
  border-bottom-color: #dddce9;
}

.permissions-tabs ::v-deep .nav-link.active {
  color: #3a3e81;
  background: transparent;
  border-bottom-color: #3a3e81;
  font-weight: 600;
}

/* ===== UTILITY ===== */
.mr-2 { margin-right: 0.5rem; }
.mr-3 { margin-right: 1rem; }
.ml-2 { margin-left: 0.5rem; }
.mb-0 { margin-bottom: 0; }
.mb-2 { margin-bottom: 0.5rem; }
.mb-3 { margin-bottom: 1rem; }
.p-0 { padding: 0; }
.mt-1 { margin-top: 0.25rem; }

/* ===== RESPONSIVE ===== */
@media (max-width: 992px) {
  .acoes-grid {
    grid-template-columns: 1fr;
  }
}
</style>
